/**
 * Manages additional Y-axes (Y2, Y3, ...) for the indicator.
 *
 * Each row exposes an AxisDropzone (drop-target for a field), a
 * show_type select (Line vs Bar overlay), and a stats_type select.
 *
 * For now extras are stored inside `config.extra_y_axes`; a follow-up
 * agent will wire the dedicated `/extra_y_axes/` endpoint.
 */

import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  type BuilderExtraYAxis,
  type BuilderFieldDef,
  type DsidCatalogResponse,
  type IndicatorBuilderConfig,
} from '../../../lib/api/reporting';
import { AxisDropzone } from './AxisDropzone';
import { STATS_TYPES, lookupField } from './AxisDesignPanel';

const SHOW_TYPES: { value: number; label: string }[] = [
  { value: 0, label: 'Line' },
  { value: 1, label: 'Bar' },
];

interface MultiYAxisPanelProps {
  config: IndicatorBuilderConfig;
  onChange: (next: IndicatorBuilderConfig) => void;
  catalog: DsidCatalogResponse | null;
}

export function MultiYAxisPanel({ config, onChange, catalog }: MultiYAxisPanelProps) {
  const extras: BuilderExtraYAxis[] = config.extra_y_axes || [];

  const updateRow = (idx: number, patch: Partial<BuilderExtraYAxis>) => {
    const next = extras.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    onChange({ ...config, extra_y_axes: next });
  };

  const removeRow = (idx: number) => {
    const next = extras.filter((_, i) => i !== idx);
    onChange({ ...config, extra_y_axes: next });
  };

  const addRow = () => {
    const blank: BuilderExtraYAxis = {
      field_dsid: 0,
      field_id: 0,
      stats_type: 1,
      show_type: 0,
    };
    onChange({ ...config, extra_y_axes: [...extras, blank] });
  };

  const setRowField = (idx: number, field: BuilderFieldDef | null) => {
    if (!field) {
      updateRow(idx, { field_dsid: 0, field_id: 0 });
      return;
    }
    updateRow(idx, { field_dsid: field.dsid, field_id: field.field_id });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          Additional Y Axes
        </Label>
        <Button type="button" variant="ghost" size="sm" onClick={addRow} className="h-7 text-xs">
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Series
        </Button>
      </div>
      {extras.length === 0 ? (
        <div className="text-xs text-muted-foreground italic px-2">
          No additional series. Drop a field to add a secondary metric.
        </div>
      ) : (
        <div className="space-y-3">
          {extras.map((row, idx) => {
            const fieldDef = lookupField(catalog, row.field_dsid, row.field_id);
            return (
              <div
                key={`extra-${idx}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start p-2 border border-border rounded"
              >
                <div className="md:col-span-6">
                  <AxisDropzone
                    axis="y_extra"
                    label={`Series Y${idx + 2}`}
                    value={fieldDef}
                    onChange={(f) => setRowField(idx, f)}
                    onClear={() => setRowField(idx, null)}
                  />
                </div>
                <div className="md:col-span-3 space-y-1">
                  <Label className="text-[10px] uppercase">Show As</Label>
                  <Select
                    value={String(row.show_type)}
                    onValueChange={(v) => updateRow(idx, { show_type: parseInt(v, 10) })}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SHOW_TYPES.map((opt) => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-[10px] uppercase">Stats</Label>
                  <Select
                    value={String(row.stats_type)}
                    onValueChange={(v) => updateRow(idx, { stats_type: parseInt(v, 10) })}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATS_TYPES.map((s) => (
                        <SelectItem key={s.value} value={String(s.value)}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-1 flex items-end justify-end h-full">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(idx)}
                    aria-label={`Remove series Y${idx + 2}`}
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
